import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { featuredEvents } from '@/mocks/events';
import { useEffect, useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [activeTab, setActiveTab] = useState<'articles' | 'events'>('articles');

  useEffect(() => {
    setActiveTab('articles');
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的赛事分类不存在。</p>
            <Link to="/" className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = getArticlesByCategory(slug || '');
  const categoryEvents = featuredEvents.filter((e) => e.category === slug);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: currentCategory.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50">
                <i className={`${currentCategory.icon} text-xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-foreground-500 text-sm mt-1">{currentCategory.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-8">
              <button
                onClick={() => setActiveTab('articles')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'articles'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-article-line mr-1.5"></i>
                相关资讯
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'events'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-live-line mr-1.5"></i>
                赛事列表
              </button>
            </div>
          </div>
        </section>

        {activeTab === 'articles' && (
          <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
            <div className="max-w-7xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 object-top"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-primary-500 bg-primary-50 px-2 py-0.5 rounded">
                            {article.categoryName}
                          </span>
                          <span className="text-xs text-foreground-400">{article.publishDate}</span>
                        </div>
                        <h2 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                          {article.title}
                        </h2>
                        <p className="text-foreground-500 text-sm line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'events' && (
          <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
            <div className="max-w-7xl mx-auto">
              {categoryEvents.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryEvents.map((event) => (
                    <div
                      key={event.id}
                      className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-all duration-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
                          {event.league}
                        </span>
                        {event.status === 'live' && (
                          <span className="flex items-center gap-1 text-xs font-semibold text-primary-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse"></span>
                            直播中
                          </span>
                        )}
                        {event.status === 'upcoming' && (
                          <span className="text-xs text-foreground-400">{event.date} {event.time}</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-base font-semibold text-foreground-900 truncate">
                          {event.teams.home}
                        </span>
                        <span className="text-sm font-bold text-foreground-400 whitespace-nowrap">VS</span>
                        <span className="text-base font-semibold text-foreground-900 truncate">
                          {event.teams.away}
                        </span>
                      </div>
                      {event.score && (
                        <div className="mt-3 text-center">
                          <span className="text-xl font-heading font-bold text-primary-500">{event.score}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-calendar-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无进行中的赛事</p>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">浏览其他分类</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50">
                      <i className={`${cat.icon} text-sm text-primary-500`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - BB体育`,
            description: currentCategory.description,
            url: `https://www.bbtiyu.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: 'BB体育',
              url: 'https://www.bbtiyu.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.bbtiyu.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCategory.name,
                item: `https://www.bbtiyu.com/category/${slug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}